import React, { useState, useEffect, useContext } from "react";
import { useMsal } from "@azure/msal-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Box } from "@mui/system";
import { Popover, CircularProgress, Typography } from "@mui/material";
import CelebrationIcon from "@mui/icons-material/Celebration";
import NotificationIcon from "../../assets/icons/NotificationIcon";
import NotificationsPanelHeader from "./NotificationsPanelHeader/NotificationsPanelHeader";
import NotificationsPanelItem from "./NotificationsPanelItem/NotificationsPanelItem";
import NotificationsPanelCarrierOffer from "./NotificationsPanelCarrierOffer/NotificationsPanelCarrierOffer";
import TertiaryButton from "../Buttons/TertiaryButton/TertiaryButton";
import RoundIndicatorIcon from "../../assets/icons/RoundIndicatorIcon";
import useNotificationsReceiver from "../../hooks/useNotificationsReceiver/useNotificationsReceiver";
import { NotificationsService } from "../../services/NotificationsService";
import { AlertContext, ActiveUserContext } from "../Dashboard/Dashboard";

export default function NotificationsPanel() {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const { notifications, setNotifications, getNotifications, continuationToken } = useNotificationsReceiver();
  const [updatedNotification, setUpdatedNotification] = useState({});
  const [unreadNotifications, setUnreadNotifications] = useState(false);
  const open = Boolean(anchorEl);
  const pid = open ? "global-search-popover" : undefined;
  const activeUser = useContext(ActiveUserContext);

  const getNotificationPanelType = (notification) => {
    switch (notification.notificationType) {
      case "CarrierOffer":
        return <NotificationsPanelCarrierOffer notification={notification} />;
      default:
        return;
    }
  };

  const handleUpdateNotification = (notification) => {
    NotificationsService.UpdateOfferNotification(instance, notification).then(res => {
      setUpdatedNotification(res);
    }).catch(error => {
      console.error(error);
      warning(error);
    });
  };

  const handleMarkAllAsRead = () => {
    if (unreadNotifications) {
      NotificationsService.UpdateAllNotificationsAsRead(instance, activeUser.userIdMcld, "CarrierOffer").catch(error => {
        console.error(error);
        warning(error);
      });
  
      const updatedNotifications = [...notifications];
      updatedNotifications.forEach(notification => {
        notification.isRead = true;
      });
  
      setNotifications(updatedNotifications);
    }
  };

  useEffect(() => {
    setUnreadNotifications(notifications.filter(n => !n.isRead).length > 0);
  }, [notifications, updatedNotification]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <TertiaryButton 
          onClick={e => setAnchorEl(e.currentTarget)}
          startIcon={NotificationIcon}
        />
        <Box sx={{ width: "20px" }}>
          {unreadNotifications &&
            <RoundIndicatorIcon sx={{ margin: "-4px 0 0 -18px", color: "error.60" }} />
          }
        </Box>
      </Box>
      <Popover
        id={pid}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
        slotProps={{
          paper: {
            sx: { boxShadow: "elevation3" },
          }
        }}
      >
        <Box sx={{ minWidth: "348px", height: "calc(100vh - 64px)" }}>
          <Box
            sx={{
              minHeight: "16px",
              position: "sticky",
              top: 0,
              backgroundColor: "white_opacity.100",
            }}
          />
          <Box sx={{ position: "sticky", top: 16 }}>
            <NotificationsPanelHeader handleMarkAllAsRead={handleMarkAllAsRead} />
          </Box>
          {notifications.length > 0 ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <InfiniteScroll
                dataLength={notifications.length}
                next={getNotifications}
                hasMore={continuationToken !== null}
                loader={
                  <Box sx={{ display: "flex", justifyContent: "center", height: "40px" }}>
                    <CircularProgress sx={{ color: "primary.60" }} size={20} />
                  </Box>
                }
                height="calc(100vh - 120px)"
              >
                <Box sx={{ margin: "0 8px 8px 8px" }}>
                  {notifications.map((notification) => (
                    <NotificationsPanelItem 
                      key={`notification-item-${notification.id}`}
                      notification={notification}
                      handleUpdateNotification={handleUpdateNotification} 
                      handleClose={() => setAnchorEl(null)}
                    >
                      {getNotificationPanelType(notification)}
                    </NotificationsPanelItem>
                  ))}
                </Box>
              </InfiniteScroll>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", margin: "24px 0 0 16px"}}>
              <Typography variant="h4">
                {"You're all caught up!"}
              </Typography>
              <CelebrationIcon sx={{ fontSize: "20px", marginLeft: "12px" }} />
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
}